/**
 * Pestaña "Centro de Contratos": búsqueda, formulario y lista.
 * Extraído de AdminPanel (App.js) sin cambios de comportamiento.
 */
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { filterBySearchTerm, sortByFolioDesc } from "./utils";
import { ContractForm } from "./ContractForm";
import { formatEventDateLong } from "../../lib/dateUtils";

const PUBLICATION_BADGE = {
  si: { label: "Sí publicar", className: "bg-green-500/20 text-green-400 border-green-500/50" },
  no: { label: "NO publicar", className: "bg-red-500/20 text-red-400 border-red-500/50" },
  pendiente: { label: "Publicación pendiente", className: "bg-amber-500/20 text-amber-400 border-amber-500/50" },
};

export function ContractsTab({
  userRole, contracts, contractsSearch, setContractsSearch,
  showContractForm, setShowContractForm, contractForm, setContractForm,
  editingContractId, contractPreview, cancelEdit, createContract, updateContract,
  startEditContract, printContractPDF, printProveedorPDF, deleteContract,
  syncFolios, DatePicker,
}) {
  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-pearl text-xl font-bold">Centro de Contratos</h2>
          <p className="text-pearl-muted text-sm">Gestiona contratos independientes con precios netos</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {userRole === "admin" && (
            <Button
              data-testid="sync-folios-btn"
              variant="outline"
              className="btn-gold-outline"
              onClick={syncFolios}
            >
              Sincronizar Folios Reales
            </Button>
          )}
          <Button onClick={() => setShowContractForm(true)} className="btn-gold" data-testid="create-contract-btn">
            Crear Contrato
          </Button>
        </div>
      </div>

      {/* Buscador en tiempo real por folio o cliente */}
      <div className="relative">
        <Input
          data-testid="contracts-search-input"
          type="text"
          placeholder="Buscar por folio (ej. PIC-356) o nombre de cliente..."
          value={contractsSearch}
          onChange={(e) => setContractsSearch(e.target.value)}
          className="input-premium pl-10"
        />
        <svg className="w-5 h-5 text-pearl-muted absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z"></path>
        </svg>
        {contractsSearch && (
          <button
            type="button"
            data-testid="contracts-search-clear"
            onClick={() => setContractsSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-pearl-muted hover:text-pearl"
          >
            ✕
          </button>
        )}
      </div>

      {/* Formulario de Contrato */}
      {showContractForm && (
        <ContractForm
          contractForm={contractForm}
          setContractForm={setContractForm}
          editingContractId={editingContractId}
          cancelEdit={cancelEdit}
          createContract={createContract}
          updateContract={updateContract}
          contractPreview={contractPreview}
          DatePicker={DatePicker}
        />
      )}

      {/* Lista de Contratos */}
      {contracts.length === 0 ? (
        <Card className="card-premium border-dashed">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-night rounded-lg flex items-center justify-center">
              <svg className="w-8 h-8 text-pearl-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            </div>
            <p className="text-pearl-muted">No hay contratos creados</p>
            <p className="text-pearl-muted/70 text-sm">Crea tu primer contrato con el boton de arriba</p>
          </CardContent>
        </Card>
      ) : (() => {
        const term = contractsSearch.trim();
        const filtered = filterBySearchTerm(contracts, term).sort(sortByFolioDesc);

        if (filtered.length === 0) {
          return (
            <Card className="card-premium border-dashed">
              <CardContent className="p-6 text-center">
                <p className="text-pearl-muted" data-testid="contracts-no-results">
                  No se encontraron contratos para "{contractsSearch}"
                </p>
              </CardContent>
            </Card>
          );
        }

        return filtered.map(contract => (
          <Card key={contract.id} className="card-premium" data-testid={`contract-card-${contract.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {contract.folio && (
                      <span
                        data-testid={`contract-folio-${contract.id}`}
                        className="text-gold font-mono font-bold text-sm bg-gold/10 border border-gold/30 px-2 py-0.5 rounded"
                      >
                        {contract.folio}
                      </span>
                    )}
                    <h3 className="text-pearl font-bold">{contract.event_name}</h3>
                    <Badge className={contract.contract_type === "special" ? "badge-gold" : "badge-gold"}>
                      {contract.contract_type === "special" ? "Especial" : "Publico"}
                    </Badge>
                    <Badge className="badge-gold">{contract.status}</Badge>
                    {(() => {
                      const pub = PUBLICATION_BADGE[contract.publication_authorization] || PUBLICATION_BADGE.pendiente;
                      return (
                        <Badge data-testid={`contract-publication-badge-${contract.id}`} className={pub.className}>{pub.label}</Badge>
                      );
                    })()}
                  </div>
                  <p className="text-pearl-muted text-sm">{contract.client_name} - {contract.client_phone}</p>
                  <p className="text-pearl-muted/70 text-sm">{formatEventDateLong(contract.event_date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-gold font-bold text-2xl">${contract.net_price?.toLocaleString()}</p>
                  <p className="text-pearl-muted text-sm">Precio Neto</p>
                  <div className="flex gap-2 mt-2 flex-wrap justify-end">
                    <Button size="sm" className="btn-gold-outline" onClick={() => startEditContract(contract)}>Editar</Button>
                    <Button size="sm" className="btn-gold" onClick={() => printContractPDF(contract)}>PDF</Button>
                    {userRole === "admin" && (
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => printProveedorPDF(contract)}>
                        Proveedor
                      </Button>
                    )}
                    <Button size="sm" variant="destructive" onClick={() => deleteContract(contract.id)}>Eliminar</Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ));
      })()}
    </>
  );
}
